import { UseFormReturn } from 'react-hook-form';
import { PlannerConfigEntityWithOptionalFields } from '@/services/configurator/PlannerConfigTypes';
import { AlertCircle, Lock, Unlock } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Form } from '@/components/ui/form';

import {
  CategorizedPhraseKey,
  getContentPageComponents,
  getPhraseCategory,
  PhraseKeysCategory,
  toContentPageSectionVisibility,
} from '@/config/phrase-contract';
import { useEffect, useState, useMemo, Fragment } from 'react';
import { BannerLargePreview } from '@/app/previewComponents/banner-large';
import { BannerMiniLinkPreview } from '@/app/previewComponents/banner-mini-link';
import { PhraseFieldSection } from '@/components/common/phrases/PhraseFieldSection';
import { PhraseFormField } from '@/components/common/phrases/PhraseFormField';
import { PreviewableToggleablePhraseFields } from '@/components/common/phrases/PreviewableToggleablePhraseFields';
import { PreviewMode } from '@/components/common/phrases/previewMode';
import { PreviewablePhraseFields } from '@/components/common/phrases/PreviewablePhraseFields';
import FormPillInputComponent from './formComponent/PillInput/FormPillInputComponent';
import { KEYWORDS_REGEX } from '@/utils/regex';
import { useProdSyncedPhraseKeys } from '@/hooks/useProdSyncedPhraseKeys';
import { useUser } from '@/components/providers/user-provider';
import { useEnv } from '@/components/providers/env-provider';

export type ActiveComponent =
  | 'contentPage'
  | 'largeBanner'
  | 'miniBanner'
  | null;

export type PhraseFormProps = {
  form: UseFormReturn<PlannerConfigEntityWithOptionalFields>;
  phraseForm: UseFormReturn<PlannerConfigEntityWithOptionalFields>;
  isFloorCalculator: boolean;
  previewMode: boolean;
  disabledFields: boolean;
  identifier?: string;
};

export function PhraseForm({
  form,
  phraseForm,
  isFloorCalculator,
  previewMode,
  disabledFields,
  identifier,
}: PhraseFormProps) {
  // Fetch phrase keys that are already synced to Prod
  const { syncedKeys: syncedPhraseKeys, error: syncedKeysError } =
    useProdSyncedPhraseKeys(identifier, !isFloorCalculator);

  const hasSyncedKeys = syncedPhraseKeys.size > 0;

  const user = useUser();
  const isAdmin = user.role === 'admin';

  const { env } = useEnv();
  const phraseProjectUrl =
    env === 'production'
      ? 'https://app.phrase.com/accounts/mammut/projects/planners/dashboard'
      : 'https://app.phrase.com/accounts/mammut/projects/do-not-translate-planners-live-cte/dashboard';
  const phraseProjectName =
    env === 'production' ? 'planners production' : 'planners CTE';

  const [
    watchedContentPageVariant,
    watchedImageUrl,
    watchedPlannerUrl,
    watchedSecondaryBannerImgUrl,
    watchedContentPageSectionVisibility,
    watchedShowInPages,
  ] = form.watch([
    'contentPageVariant',
    'imageUrl',
    'plannerUrl',
    'secondaryBannerImgUrl',
    'contentPageSectionVisibility',
    'showInPages',
  ]);
  const contentPageVisible = watchedShowInPages?.includes('content-page');

  const [activeComponent, setActiveComponent] =
    useState<ActiveComponent>('miniBanner');

  const watchedPhraseStrings = phraseForm.watch('phraseStrings');
  const watchedForm = form.watch();

  const toggleableSections: Array<CategorizedPhraseKey> = useMemo(() => {
    const sections: Array<CategorizedPhraseKey> = [
      'page-secondary-banner',
      'page-primary-card',
      'page-secondary-card',
    ];
    if (watchedContentPageVariant === 'banners_with_video_and_external_link') {
      sections.push('video');
    }
    return sections;
  }, [watchedContentPageVariant]);

  // This is to show the correct phrase translations and sections for each category
  const {
    nonContentPageCategories,
    contentPageCategories,
    allCategories,
  }: {
    nonContentPageCategories: PhraseKeysCategory[];
    contentPageCategories: PhraseKeysCategory[];
    allCategories: PhraseKeysCategory[];
  } = useMemo(() => {
    const nonContent = new Set<PhraseKeysCategory>();
    const content = new Set<PhraseKeysCategory>();
    const showInPages = watchedShowInPages || [];

    if (showInPages.includes('pip')) {
      nonContent.add('keywords');
      nonContent.add('cta');
      nonContent.add('banner_small');
      nonContent.add('banner_large');
    }

    if (showInPages.includes('plp')) {
      nonContent.add('keywords');
      nonContent.add('cta');
      nonContent.add('banner_large');
    }

    if (contentPageVisible) {
      nonContent.add('keywords');
      const contentPageVariation = getContentPageComponents(
        watchedContentPageVariant,
      );
      // Get all phrase keys from all components and add them directly
      Object.keys(contentPageVariation).forEach((componentKey) => {
        content.add(componentKey as CategorizedPhraseKey);
      });
    }

    return {
      nonContentPageCategories: Array.from(nonContent),
      contentPageCategories: Array.from(content),
      allCategories: Array.from([...nonContent, ...content]),
    };
  }, [contentPageVisible, watchedShowInPages, watchedContentPageVariant]);

  useEffect(() => {
    const phraseStrings = phraseForm.getValues().phraseStrings ?? {};
    if (!phraseStrings) {
      return;
    }

    const enabledCategories = (allCategories as PhraseKeysCategory[]).filter(
      (category) => {
        const visibilityKey = toContentPageSectionVisibility(
          category as CategorizedPhraseKey,
        );

        if (!visibilityKey) {
          return true;
        }

        return watchedContentPageSectionVisibility?.[visibilityKey] !== false;
      },
    );

    const getTransformedPhraseStrings = () => {
      return enabledCategories
        .map((category) =>
          getPhraseCategory({
            component: category,
          }),
        )
        .flatMap((cat) => cat.phraseKeys || [])
        .reduce(
          (acc, phraseKey) => ({
            ...acc,
            [phraseKey]: phraseStrings[phraseKey] || '',
          }),
          {},
        );
    };

    const transformedPhraseStrings = getTransformedPhraseStrings();
    phraseForm.resetField('phraseStrings', {
      defaultValue: transformedPhraseStrings,
    });
  }, [
    phraseForm.getValues,
    allCategories,
    phraseForm.resetField,
    watchedContentPageSectionVisibility,
  ]);

  if (isFloorCalculator) {
    return null;
  }

  useEffect(() => {
    if (watchedContentPageVariant === 'inject_current_page') {
      form.setValue('contentPageSectionVisibility', {
        pagePrimaryCard: false,
        pageSecondaryBanner: false,
        pageSecondaryCard: false,
        video: false,
      });
    }
    if (watchedContentPageVariant === 'banners_with_external_link') {
      form.setValue('contentPageSectionVisibility', {
        pagePrimaryCard: true,
        pageSecondaryBanner: true,
        pageSecondaryCard: true,
        video: false,
      });
    }
    if (watchedContentPageVariant === 'banners_with_video_and_external_link') {
      form.setValue('contentPageSectionVisibility', {
        pagePrimaryCard: true,
        pageSecondaryBanner: true,
        pageSecondaryCard: true,
        video: true,
      });
    }
  }, [watchedContentPageVariant]);

  return (
    <Form {...phraseForm}>
      <form className='flex flex-col w-full'>
        {syncedKeysError && (
          <Alert className='mb-4 border-red-600 bg-red-600 text-white'>
            <AlertCircle className='h-4 w-4' />
            <AlertTitle className='font-bold'>
              Error Loading Translation State
            </AlertTitle>
            <AlertDescription className='text-white'>
              Failed to fetch synced translation keys from Phrase. All
              translation fields have been disabled to prevent accidental data
              loss. Please try refreshing the page.
            </AlertDescription>
          </Alert>
        )}
        {hasSyncedKeys && !previewMode && (
          <Alert className='mb-4 border-amber-500 bg-amber-500 text-white'>
            {isAdmin ? (
              <Unlock className='h-4 w-4 text-muted-foreground' />
            ) : (
              <Lock className='h-4 w-4 text-muted-foreground' />
            )}
            <AlertTitle className='font-bold'>
              {!isAdmin && 'The translation fields are locked'}
              {isAdmin &&
                'Fields are locked for normal users, be very careful when editing as it might trigger unwanted updates in Phrase like destroying SEO'}
            </AlertTitle>
            <AlertDescription className='text-white'>
              {!isAdmin && (
                <>
                  {' '}
                  Fields marked with a lock icon (🔒) have already been
                  published to Phrase and cannot be edited here. To update these
                  translations, please edit them directly in the{' '}
                  <a
                    href={phraseProjectUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='underline font-semibold'
                  >
                    Phrase ({phraseProjectName})
                  </a>
                  . New translation fields can still be edited and will become
                  read-only after publishing.
                </>
              )}
              {isAdmin && (
                <>
                  Fields marked with a lock icon (🔒) are locked for normal
                  users. Be very careful when editing these fields as it might
                  trigger unwanted updates in Phrase, affecting SEO aka
                  destroying translations.
                </>
              )}
            </AlertDescription>
          </Alert>
        )}
        {!previewMode && (
          <>
            <Alert className='mb-4 border-slate-800 bg-slate-800  text-white'>
              <AlertCircle className='h-4 w-4' />
              <AlertTitle className='font-bold'>Preview Mode</AlertTitle>
              <AlertDescription className='text-white'>
                The components are rendered in a preview mode and may not look
                exactly like the final version. Changes made here will be
                visible dynamically by entering the phrase strings.
              </AlertDescription>
            </Alert>
            <Alert className='mb-4 border-indigo-400 bg-indigo-400 text-white'>
              <AlertCircle className='h-4 w-4' />
              <AlertTitle className='font-bold'>
                The strings below need to be in ENGLISH and will become visible
                in Phrase to be translated to the local language.
              </AlertTitle>
            </Alert>
          </>
        )}
        <>
          <PreviewMode
            previewMode={previewMode}
            activeComponent={activeComponent}
            phraseStrings={watchedPhraseStrings ?? {}}
            contentPageVariant={watchedContentPageVariant}
            imageUrl={watchedImageUrl}
            plannerUrl={watchedPlannerUrl}
            secondaryBannerImgUrl={watchedSecondaryBannerImgUrl}
            contentPageVisible={contentPageVisible}
            dynamicContentPageSectionVisibility={
              watchedContentPageSectionVisibility
            }
            dynamicShowInPages={watchedShowInPages}
            setActiveComponent={setActiveComponent}
          />
          {!previewMode && (
            <>
              {nonContentPageCategories.includes('keywords') && (
                <FormPillInputComponent
                  formControl={phraseForm.control}
                  name='phraseStrings.keywords'
                  regexpValidation={KEYWORDS_REGEX}
                  formLabel={getPhraseCategory({ component: 'keywords' }).text}
                  formDescription={
                    getPhraseCategory({ component: 'keywords' }).description ||
                    ''
                  }
                  placeholder='Enter keyword'
                  disabled={disabledFields}
                  badgeSize={8}
                  syncedPhraseKeys={syncedPhraseKeys}
                />
              )}
              {nonContentPageCategories.includes('cta') && (
                <>
                  <PhraseFieldSection
                    sectionTitle={getPhraseCategory({ component: 'cta' }).text}
                    sectionDescription={
                      getPhraseCategory({ component: 'cta' }).description
                    }
                    sectionDescriptionVisible
                    sectionClassName='mt-8'
                    renderSectionContents={() => (
                      <PhraseFormField
                        phraseKey='cta'
                        phraseFormControl={phraseForm.control}
                        disabled={disabledFields || !!syncedKeysError}
                        syncedPhraseKeys={syncedPhraseKeys}
                      />
                    )}
                  />
                </>
              )}
              <div>
                {(nonContentPageCategories as PhraseKeysCategory[])
                  .filter((cat) => cat !== 'keywords' && cat !== 'cta')
                  .map((catItem) => (
                    <Fragment key={catItem}>
                      <div className='flex flex-row gap-8 mt-8'>
                        <PhraseFieldSection
                          sectionTitle={
                            getPhraseCategory({ component: catItem }).text
                          }
                          sectionDescription={
                            getPhraseCategory({ component: catItem })
                              .description
                          }
                          sectionDescriptionVisible
                          sectionClassName='w-1/2'
                          renderSectionContents={() => {
                            return (
                              <Fragment>
                                {getPhraseCategory({
                                  component: catItem,
                                }).phraseKeys.map((phraseKey) => {
                                  return (
                                    <Fragment key={phraseKey}>
                                      <PhraseFormField
                                        phraseKey={phraseKey}
                                        phraseFormControl={phraseForm.control}
                                        disabled={
                                          disabledFields || !!syncedKeysError
                                        }
                                        syncedPhraseKeys={syncedPhraseKeys}
                                      />
                                    </Fragment>
                                  );
                                })}
                              </Fragment>
                            );
                          }}
                        />
                        {watchedShowInPages?.includes('pip') &&
                          catItem === 'banner_small' && (
                            <BannerMiniLinkPreview
                              phraseStrings={watchedPhraseStrings ?? {}}
                              headerVisible
                              showInPages={watchedShowInPages}
                            />
                          )}
                        {(watchedShowInPages?.includes('pip') ||
                          watchedShowInPages?.includes('plp')) &&
                          catItem === 'banner_large' && (
                            <BannerLargePreview
                              phraseStrings={watchedPhraseStrings ?? {}}
                              headerVisible
                              showInPages={watchedShowInPages}
                              imageUrl={form.getValues().imageUrl}
                            />
                          )}
                      </div>
                    </Fragment>
                  ))}
              </div>
            </>
          )}
          {contentPageVisible && !previewMode && (
            <>
              {contentPageCategories.includes('page-meta') && (
                <div className='w-full mt-8'>
                  <PhraseFieldSection
                    sectionTitle={
                      getPhraseCategory({ component: 'page-meta' }).text
                    }
                    sectionDescription={
                      getPhraseCategory({ component: 'page-meta' }).description
                    }
                    sectionDescriptionVisible
                    renderSectionContents={() => (
                      <Fragment>
                        <PhraseFormField
                          phraseKey='page-meta-title'
                          phraseFormControl={phraseForm.control}
                          disabled={disabledFields || !!syncedKeysError}
                          syncedPhraseKeys={syncedPhraseKeys}
                        />
                        <PhraseFormField
                          phraseKey='page-meta-description'
                          phraseFormControl={phraseForm.control}
                          disabled={disabledFields || !!syncedKeysError}
                          syncedPhraseKeys={syncedPhraseKeys}
                        />
                      </Fragment>
                    )}
                  />
                </div>
              )}
              <PreviewablePhraseFields
                categorizedPhraseKey='page-intro'
                phraseFormControl={phraseForm.control}
                watchedPhraseStrings={watchedPhraseStrings ?? {}}
                watchedPlanner={watchedForm}
                disableFields={disabledFields || !!syncedKeysError}
                syncedPhraseKeys={syncedPhraseKeys}
              />
              <>
                <PreviewablePhraseFields
                  categorizedPhraseKey='page-primary-banner'
                  phraseFormControl={phraseForm.control}
                  watchedPhraseStrings={watchedPhraseStrings ?? {}}
                  watchedPlanner={watchedForm}
                  disableFields={disabledFields || !!syncedKeysError}
                  syncedPhraseKeys={syncedPhraseKeys}
                  className={
                    watchedContentPageVariant === 'inject_current_page'
                      ? 'hidden'
                      : 'visible'
                  }
                />
                <PreviewableToggleablePhraseFields
                  phraseKey='page-secondary-banner'
                  sectionKey='contentPageSectionVisibility.pageSecondaryBanner'
                  formControl={form.control}
                  phraseForm={phraseForm}
                  watchedPhraseStrings={watchedPhraseStrings ?? {}}
                  watchedPlanner={watchedForm}
                  disableFields={disabledFields || !!syncedKeysError}
                  syncedPhraseKeys={syncedPhraseKeys}
                  className={
                    toggleableSections.includes('page-secondary-banner')
                      ? 'visible'
                      : 'hidden'
                  }
                />
                <PreviewableToggleablePhraseFields
                  phraseKey='page-primary-card'
                  sectionKey='contentPageSectionVisibility.pagePrimaryCard'
                  formControl={form.control}
                  phraseForm={phraseForm}
                  watchedPhraseStrings={watchedPhraseStrings ?? {}}
                  watchedPlanner={watchedForm}
                  disableFields={disabledFields || !!syncedKeysError}
                  syncedPhraseKeys={syncedPhraseKeys}
                  className={
                    toggleableSections.includes('page-primary-card')
                      ? 'visible'
                      : 'hidden'
                  }
                />
                <PreviewableToggleablePhraseFields
                  phraseKey='page-secondary-card'
                  sectionKey='contentPageSectionVisibility.pageSecondaryCard'
                  formControl={form.control}
                  phraseForm={phraseForm}
                  watchedPhraseStrings={watchedPhraseStrings ?? {}}
                  watchedPlanner={watchedForm}
                  disableFields={disabledFields || !!syncedKeysError}
                  syncedPhraseKeys={syncedPhraseKeys}
                  className={
                    toggleableSections.includes('page-secondary-card')
                      ? 'visible'
                      : 'hidden'
                  }
                />
                <PreviewableToggleablePhraseFields
                  phraseKey='video'
                  sectionKey='contentPageSectionVisibility.video'
                  formControl={form.control}
                  phraseForm={phraseForm}
                  watchedPhraseStrings={watchedPhraseStrings ?? {}}
                  watchedPlanner={watchedForm}
                  disableFields={disabledFields || !!syncedKeysError}
                  syncedPhraseKeys={syncedPhraseKeys}
                  className={
                    toggleableSections.includes('video') ? 'visible' : 'hidden'
                  }
                />
              </>
            </>
          )}
        </>
      </form>
    </Form>
  );
}
